// ABOUTME: Renders the attachment chips for a chat update.
// ABOUTME: Inline image preview for image attachments, file name only otherwise; click opens the photo viewer or downloads the file.

import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import Text from "../../../../../../../components/theme/text/Text";
import { tokens } from "../../../../../../../theme";
import MPhotoViewer from "../../../../../../../components/global/Modals/MPhotoViewer";
import { downloadFile } from "../../../../../../../utils/downloadFile";

const MessageTypeAttachments = ({ update }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    // Only image attachments go to the photo viewer; originalIndex ties a chip
    // back to its viewer entry even when two attachments share a url
    const imageAttachments = useMemo(() =>
        update.attachments
            .map((attachment, originalIndex) => ({ ...attachment, originalIndex }))
            .filter((attachment) => attachment.type === 'image'),
    [update.attachments]);

    const photosForViewer = useMemo(() =>
        imageAttachments.map((attachment) => ({
            id: attachment.originalIndex,
            url: attachment.url,
            fileName: attachment.fileName,
        })),
    [imageAttachments]);

    // Keep the viewer in range when a refetch shrinks the attachment list
    useEffect(() => {
        if (photoViewerOpen && currentPhotoIndex >= photosForViewer.length) {
            if (photosForViewer.length === 0) {
                setPhotoViewerOpen(false);
                setCurrentPhotoIndex(0);
            } else {
                setCurrentPhotoIndex(photosForViewer.length - 1);
            }
        }
    }, [photoViewerOpen, currentPhotoIndex, photosForViewer.length]);

    const handleAttachmentClick = (attachment, attachmentIndex) => {
        if (attachment.type === 'image') {
            const imageIndex = imageAttachments.findIndex((img) => img.originalIndex === attachmentIndex);
            if (imageIndex !== -1) {
                setCurrentPhotoIndex(imageIndex);
                setPhotoViewerOpen(true);
            }
        } else {
            downloadFile(attachment.url, attachment.fileName);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {update.attachments.map((attachment, attachmentIndex) => (
                <Box
                    key={attachmentIndex}
                    onClick={() => handleAttachmentClick(attachment, attachmentIndex)}
                    sx={{
                        height: 32,
                        borderRadius: 4,
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                        border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                        pl: 0.5,
                        pr: 2,
                        display: "flex",
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                    }}
                >
                    {attachment.type === 'image' && (
                        <img
                            style={{
                                height: "24px",
                                minWidth: "24px",
                                objectFit: "cover",
                                objectPosition: "center",
                                borderRadius: "12px",
                            }}
                            src={attachment.url}
                            alt={attachment.fileName}
                        />
                    )}
                    <Text noBreak fontSize={13}>{attachment.fileName}</Text>
                </Box>
            ))}

            <MPhotoViewer
                open={photoViewerOpen}
                onClose={() => setPhotoViewerOpen(false)}
                photos={photosForViewer}
                currentPhotoIndex={currentPhotoIndex}
                onPhotoChange={setCurrentPhotoIndex}
                showDownload
            />
        </Box>
    );
};

export default MessageTypeAttachments;
